import React, { useCallback, useEffect, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import { useAppSelector } from '../../app/hooks';
import { selectAnalysisData, selectDomainCss } from '../analysis/AnalysisSlice';
import { Button, Grid } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ReactComponent as Google } from '../../assets/google-maps.svg';
import { ReactComponent as Zillow } from '../../assets/zillow.svg';

// https://mui.com/material-ui/react-table/#sorting-amp-selecting

export interface CompTableData {
  id: string;
  address: string;
  city: string;
  county: string;
  state: string;
  formattedAddress: string;
  bathrooms: number;
  bedrooms: number;
  squareFootage: number;
  correlation: number;
  daysOld: number;
  distance: number;
  latitude: number;
  longitute: number;
  price: number;
  ppsqft: number;
  propertyType: string;
  publishedDate: string;
}


function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof CompTableData;
  label: string;
  numeric: boolean;
}



const DEFAULT_ORDER = 'desc';
const DEFAULT_ORDER_BY = 'correlation';
const DEFAULT_ROWS_PER_PAGE = 25;

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof CompTableData) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  domainCss: any;
  mode?: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, domainCss, onRequestSort } =
    props;
  const createSortHandler =
    (newOrderBy: keyof CompTableData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'address',
      numeric: false,
      disablePadding: true,
      label: 'Address',
    },
    {
      id: 'correlation',
      numeric: true,
      disablePadding: false,
      label: props.mode === 'print' ? 'Corrl.' : 'Correlation',
    },
    {
      id: 'distance',
      numeric: true,
      disablePadding: false,
      label: props.mode === 'print' ? 'Dist (mi)' : 'Distance (mi)',
    },
    {
      id: 'price',
      numeric: true,
      disablePadding: false,
      label: 'Price',
    },
    {
      id: 'ppsqft',
      numeric: true,
      disablePadding: false,
      label: '$/sqft',
    },
    {
      id: 'bedrooms',
      numeric: true,
      disablePadding: false,
      label: props.mode === 'print' ? 'Bd' : 'Bed',
    },
    {
      id: 'bathrooms',
      numeric: true,
      disablePadding: false,
      label: props.mode === 'print' ? 'Bth' : 'Bath',
    },
    {
      id: 'squareFootage',
      numeric: true,
      disablePadding: false,
      label: props.mode === 'print' ? 'Sft' : 'Sqft',
    },
    {
      id: 'publishedDate',
      numeric: false,
      disablePadding: false,
      label: 'Date',
    }
  ];

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: domainCss?.backdropBG, border: 1, borderColor: grey[300] }}>
        <TableCell padding="checkbox">
          <Checkbox
            sx={{ display: 'none' }}
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontSize: `${props.mode === 'print' ? 10 : 14}px` }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead >
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Rental Comps
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

export interface CompTableProps {
  mode?: string;
}

export default function CompTable(props: CompTableProps) {
  const analysisData = useAppSelector(selectAnalysisData)
  const domainCss = useAppSelector(selectDomainCss)
  const [order, setOrder] = useState<Order>(DEFAULT_ORDER)
  const [orderBy, setOrderBy] = useState<keyof CompTableData>(DEFAULT_ORDER_BY)
  const [selected, setSelected] = useState<readonly string[]>([])
  const [page, setPage] = useState(0)
  const [dense, setDense] = useState(true)
  const [visibleRows, setVisibleRows] = useState<CompTableData[] | null>(null)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [paddingHeight, setPaddingHeight] = useState(0)

  useEffect(() => {
    let rowsOnMount = stableSort(
      analysisData?.comps?.listings as CompTableData[],
      getComparator(DEFAULT_ORDER, DEFAULT_ORDER_BY),
    );
    rowsOnMount = rowsOnMount.slice(
      0 * DEFAULT_ROWS_PER_PAGE,
      0 * DEFAULT_ROWS_PER_PAGE + DEFAULT_ROWS_PER_PAGE,
    );

    setVisibleRows(rowsOnMount);
  }, []);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof CompTableData) => {
      const isAsc = orderBy === newOrderBy && order === 'asc';
      const toggledOrder = isAsc ? 'desc' : 'asc';
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);

      const sortedRows = stableSort(analysisData?.comps?.listings as CompTableData[], getComparator(toggledOrder, newOrderBy));
      const updatedRows = sortedRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
      setVisibleRows(updatedRows);
    },
    [order, orderBy, page, rowsPerPage],
  );

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = analysisData?.comps?.listings.map((n: any) => n.address);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    // setSelected(newSelected);
  };

  const handleChangePage = React.useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);

      const sortedRows = stableSort(analysisData?.comps?.listings as CompTableData[], getComparator(order, orderBy));
      const updatedRows = sortedRows.slice(
        newPage * rowsPerPage,
        newPage * rowsPerPage + rowsPerPage,
      );
      setVisibleRows(updatedRows);

      // Avoid a layout jump when reaching the last page with empty rows.
      const numEmptyRows =
        newPage > 0 ? Math.max(0, (1 + newPage) * rowsPerPage - analysisData?.comps?.listings.length) : 0;

      const newPaddingHeight = (dense ? 33 : 53) * numEmptyRows;
      setPaddingHeight(newPaddingHeight);
    },
    [order, orderBy, dense, rowsPerPage],
  );

  const handleChangeRowsPerPage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(updatedRowsPerPage);

      setPage(0);

      const sortedRows = stableSort(analysisData?.comps?.listings as CompTableData[], getComparator(order, orderBy));
      const updatedRows = sortedRows.slice(
        0 * updatedRowsPerPage,
        0 * updatedRowsPerPage + updatedRowsPerPage,
      );
      setVisibleRows(updatedRows);

      // There is no layout jump to handle on the first page.
      setPaddingHeight(0);
    },
    [order, orderBy],
  );

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  return (
    <TableContainer>
      <Table>
        <EnhancedTableHead
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={analysisData?.comps?.listings.length}
          domainCss={domainCss}
          mode={props.mode}
        />
        <TableBody>
          {visibleRows
            ? visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.address);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  onClick={(event) => handleClick(event, row.address)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={index}
                  selected={isItemSelected}
                  sx={{ cursor: 'auto' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      sx={{ display: 'none' }}
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    padding="none"
                  ><Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      <Typography variant="body1" component="span" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>
                        {row.address}
                      </Typography>
                      <Button variant='text' size='small' href={`https://zillow.com/homes/${row.formattedAddress}`} target="_blank" sx={{ minWidth: 'auto' }} className='print-hide'>
                        <Zillow width={16} height={16} />
                      </Button>
                      <Button variant='text' size='small' href={`https://www.google.com/maps/place/${row.formattedAddress}`} target="_blank" sx={{ minWidth: 'auto' }} className='print-hide'>
                        <Google width={16} height={16} />
                      </Button></Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{(row.correlation * 100).toFixed(2)} %</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{parseFloat((row.distance + '')).toPrecision(2)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>${isFinite(row.price) ? row.price.toLocaleString() : row.price}</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>${row.ppsqft ? (row.ppsqft).toPrecision(2) : '~'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{row.bedrooms}</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{row.bathrooms}</TableCell>
                  <TableCell align="right" sx={{ fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{isFinite(row.squareFootage) ? row.squareFootage.toLocaleString() : row.squareFootage}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: `${props.mode === 'print' ? 10 : 16}px` }}>{row.publishedDate?.substring(0, 10)}</TableCell>
                </TableRow>
              );
            })
            : null}
          {paddingHeight > 0 && (
            <TableRow
              style={{
                height: paddingHeight,
              }}
            >
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer >
  )
}